/**
 * Per-user API keys for the TEND MCP server (/api/auth/api-keys).
 *
 * Gateway-owned, JWT only. A person manages their own keys; a SuperAdmin
 * may mint for another user (user_id). The brand list is explicit and
 * never wider than what the target user can reach.
 */
import { API_BASE_URL } from '../config/api';
import { authedFetch } from '../utils/authedFetch';

const BASE = `${API_BASE_URL}/auth/api-keys`;

export type ApiKeyScope = 'read' | 'write' | 'queue' | 'publish';

export interface ApiKeyRow {
  id: number;
  user_id: string;
  name: string;
  /** `tend_` + 8 chars — what the list shows; never the secret. */
  key_prefix: string;
  scopes: ApiKeyScope[];
  brand_ids: number[];
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string | null;
  created_by: string | null;
}

export interface MintApiKeyRequest {
  name: string;
  brand_ids: number[];
  scopes?: ApiKeyScope[];
  expires_days?: number;
  /** SuperAdmin only: mint on behalf of another user. */
  user_id?: string;
}

/** The one and only time the token comes back. */
export interface MintApiKeyResponse {
  key: ApiKeyRow;
  token: string;
}

const jsonHeaders = (): Record<string, string> => ({ 'Content-Type': 'application/json' });

/** Surface FastAPI's `detail` (string or validation list) as the error text. */
const fail = async (res: Response, fallback: string): Promise<never> => {
  let message = fallback;
  try {
    const body = await res.json();
    const detail = body?.detail;
    if (Array.isArray(detail)) message = detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
    else if (typeof detail === 'string') message = detail;
  } catch {
    /* non-JSON body: keep the fallback */
  }
  throw new Error(message);
};

/** `userId` lists another user's keys (SuperAdmin only; 403 otherwise). */
export const listApiKeys = async (includeRevoked = false, userId?: string): Promise<ApiKeyRow[]> => {
  const params = new URLSearchParams({ include_revoked: includeRevoked ? 'true' : 'false' });
  if (userId) params.set('user_id', userId);
  const res = await authedFetch(`${BASE}?${params.toString()}`);
  if (!res.ok) return fail(res, 'Could not load API keys');
  const body = await res.json();
  return (body?.keys ?? []) as ApiKeyRow[];
};

export const mintApiKey = async (payload: MintApiKeyRequest): Promise<MintApiKeyResponse> => {
  const res = await authedFetch(BASE, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) });
  if (!res.ok) return fail(res, 'Could not create the key');
  return (await res.json()) as MintApiKeyResponse;
};

export const updateApiKeyBrands = async (keyId: number, brandIds: number[]): Promise<ApiKeyRow> => {
  const res = await authedFetch(`${BASE}/${keyId}/brands`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ brand_ids: brandIds }),
  });
  if (!res.ok) return fail(res, 'Could not update the key');
  const body = await res.json();
  return body.key as ApiKeyRow;
};

export const revokeApiKey = async (keyId: number): Promise<ApiKeyRow> => {
  const res = await authedFetch(`${BASE}/${keyId}`, { method: 'DELETE' });
  if (!res.ok) return fail(res, 'Could not revoke the key');
  const body = await res.json();
  return body.key as ApiKeyRow;
};

/** The two connection recipes, with the token filled in. */
export const connectionRecipes = (origin: string, token: string) => ({
  mcpUrl: `${origin}/api/mcp`,
  claudeCode: `claude mcp add --transport http tend ${origin}/api/mcp --header "Authorization: Bearer ${token}"`,
  header: `Authorization: Bearer ${token}`,
});
